import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "@/hooks/use-toast";
import { fastApiClient, type CancelSubscriptionResponse } from "@/api/fastApiClient";

interface SubscriptionStatus {
  isSubscribed: boolean;
  plan: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** True when this user previously redeemed an Education Partner Pilot code and hasn't
   * already used the EDUPARTNER20 discount — surfaces the continuation banner on account/pricing. */
  eligibleForEducationDiscount: boolean;
  loading: boolean;
}

/**
 * Proof of the consumer's affirmative consent to this plan's automatic renewal terms.
 * Sent with the checkout request and retained server-side (see `AutoRenewalTermsConfig` in
 * `config/product.ts` for the FL-specific disclosure content and its legal-review status).
 */
export interface AutoRenewalConsent {
  accepted_auto_renewal: boolean;
  accepted_terms: boolean;
  terms_version: string;
  plan_id: string;
  disclosure_text: string;
}

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus;
  createCheckoutSession: (hoaName: string, plan?: string, promoCode?: string, consent?: AutoRenewalConsent) => Promise<string>;
  createCustomerPortalSession: () => Promise<string>;
  cancelSubscription: () => Promise<CancelSubscriptionResponse>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}

const FRONTEND_BASE_URL = typeof window !== "undefined" ? window.location.origin : "https://www.floridahoalaw.ai";

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    isSubscribed: false,
    plan: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    eligibleForEducationDiscount: false,
    loading: true,
  });

  useEffect(() => {
    async function fetchSubscriptionStatus() {
      if (!currentUser) {
        setSubscriptionStatus({
          isSubscribed: false,
          plan: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          eligibleForEducationDiscount: false,
          loading: false,
        });
        return;
      }
      setSubscriptionStatus((prev) => ({ ...prev, loading: true }));
      try {
        const res = await fastApiClient.getSubscriptionStatus();
        setSubscriptionStatus({
          isSubscribed: res.is_active || false,
          plan: res.status || null,
          currentPeriodEnd: res.end_date || null,
          cancelAtPeriodEnd: res.cancel_at_period_end || false,
          // Defaults false until the backend's Education Partner Pilot redemption table exists
          // and is populated — see the Phase 2/Phase 3 split in the plan.
          eligibleForEducationDiscount: res.eligible_for_education_discount || false,
          loading: false,
        });
      } catch {
        toast({
          title: "Error",
          description: "Failed to load subscription information",
          variant: "destructive",
        });
        setSubscriptionStatus({
          isSubscribed: false,
          plan: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          eligibleForEducationDiscount: false,
          loading: false,
        });
      }
    }
    void fetchSubscriptionStatus();
  }, [currentUser, toast]);

  async function createCheckoutSession(
    hoaName: string,
    plan: string = "monthly",
    promoCode?: string,
    consent?: AutoRenewalConsent
  ): Promise<string> {
    if (!currentUser) {
      throw new Error("User must be logged in");
    }
    toast({
      title: "Redirecting to checkout",
      description: "You will be redirected to the payment page",
    });
    return fastApiClient.createCheckoutSession({
      success_url: `${FRONTEND_BASE_URL}/chatbot`,
      cancel_url: `${FRONTEND_BASE_URL}/pricing`,
      hoa_name: hoaName,
      plan,
      promo_code: promoCode || null,
      consent,
    });
  }

  async function createCustomerPortalSession(): Promise<string> {
    if (!currentUser) {
      throw new Error("User must be logged in");
    }
    toast({
      title: "Redirecting to customer portal",
      description: "You will be redirected to manage your subscription",
    });
    try {
      return await fastApiClient.createCustomerPortalSession(`${FRONTEND_BASE_URL}/profile`);
    } catch (error) {
      console.error("Failed to create customer portal session:", error);
      throw new Error("Failed to create customer portal session");
    }
  }

  async function cancelSubscription(): Promise<CancelSubscriptionResponse> {
    if (!currentUser) {
      throw new Error("User must be logged in");
    }

    try {
      const res = await fastApiClient.cancelSubscription();

      const statusRes = await fastApiClient.getSubscriptionStatus();
      setSubscriptionStatus({
        isSubscribed: statusRes.is_active || false,
        plan: statusRes.status || null,
        currentPeriodEnd: statusRes.end_date || null,
        cancelAtPeriodEnd: statusRes.cancel_at_period_end || false,
        eligibleForEducationDiscount: statusRes.eligible_for_education_discount || false,
        loading: false,
      });

      toast({
        title: "Subscription Cancelled",
        description:
          res.message || "Your subscription will remain active until the end of the billing period.",
      });

      return res;
    } catch (error: unknown) {
      const err = error as { message?: string };
      const errorMessage = err.message || "Failed to cancel subscription";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw new Error(errorMessage);
    }
  }

  const value = {
    subscriptionStatus,
    createCheckoutSession,
    createCustomerPortalSession,
    cancelSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
